import React, {Fragment} from 'react';
import Typography from '@mui/material/Typography';
import type {DataLoadStatus, Media} from 'api/types';
import {useLocalization} from 'localization';
import {ColorPalette} from 'theme';
import {usePreviewFiles} from 'services/metadata/hooks/usePreviewFiles';
import {useHandleDelayedCall} from 'services/utils';
import {ActivityIndicator, Card, Spacer, ToolBar} from 'components';
import {useTextValue} from 'schema/preview/hooks';
import {FileCard} from './FileCard';
import {ManifestationPanelContainer} from './ManifestationPanelContainer';

type Props = {
  files: DataLoadStatus<Media[]>;
  manifestationId: string;
  mock?: boolean;
  onClose: () => void;
  refreshMedia: () => void;
  trykkunderlagReserved?: boolean;
};

export const FilesPanel: React.FC<Props> = ({
  files,
  manifestationId,
  mock,
  onClose,
  refreshMedia,
  trykkunderlagReserved,
}) => {
  const data = files.data;
  const TextValue = useTextValue();
  const {t} = useLocalization();

  const {previewAll} = usePreviewFiles(files.data, mock);
  const handlePreviewAllDelayed = useHandleDelayedCall(previewAll);

  const OpenAllComponent = (
    <ToolBar.ControlledIconTool
      icon="Preview"
      size="small"
      onClick={handlePreviewAllDelayed.handleCall}
      isCalling={handlePreviewAllDelayed.isCalling}
      isDelayed={handlePreviewAllDelayed.isDelayed}
    />
  );

  return (
    <ManifestationPanelContainer
      title={t('page.metadata.manifestation.tools.files')}
      icon={'Files'}
      onClose={onClose}
      extraButtons={[OpenAllComponent]}>
      {trykkunderlagReserved ? (
        <Card colorx={ColorPalette.primary.white}>
          <TextValue>{t('error.files.reserved')}</TextValue>
        </Card>
      ) : null}

      {files.status === 'NotLoaded' || files.status === 'Loading' ? (
        <Card colorx={ColorPalette.primary.white}>
          <ActivityIndicator />
        </Card>
      ) : files.status === 'Failed' ? (
        <Card colorx={ColorPalette.primary.white}>
          <Typography variant="body1" color={ColorPalette.error}>
            {t('error.files.failedToLoad')}
          </Typography>
        </Card>
      ) : Array.isArray(data) && data.length > 0 ? (
        data.map((f, idx, data) => (
          <Fragment key={f.id}>
            <FileCard
              file={f}
              manifestationId={manifestationId}
              mock={mock}
              refreshMedia={refreshMedia}
            />
            {idx + 1 < data.length ? <Spacer size={1} /> : null}
          </Fragment>
        ))
      ) : (
        <Card colorx={ColorPalette.primary.white}>
          <TextValue>{t('error.files.empty')}</TextValue>
        </Card>
      )}
    </ManifestationPanelContainer>
  );
};
